// @ts-check
import { gql, useQuery } from '@apollo/client';
import { Flex } from '~/design-system';
import { fieldsToImport } from '../../../pages/imports/partnerAliasImport/$importId/_config';
import ChangesPreviewTable from '../ChangesPreviewTable';
import ChangesPreviewTableSkeleton from '../ChangesPreviewTableSkeleton';

/**
 * The props that this component needs.
 * @typedef {object} ReadyStepProps
 * @property {object} importData
 * @property {string} updateOptions
 */

/**
 * Renders the review import screen with change details
 * The view. The props are passed in from useReviewImport - all the logic resides there.
 * @param {ReturnType<useReviewImport>} props
 * @returns
 */
export default function ReviewImport({ importData = {}, loading }) {
  if (loading) return <ChangesPreviewTableSkeleton />;

  const alias = importData.preview.find(
    (previewItem) => previewItem.object === 'ALIAS'
  );

  return (
    <Flex sx={{ flexDirection: 'column', padding: '0px 15% 30px 15%' }}>
      <ChangesPreviewTable
        rows={alias.changes}
        importData={importData}
        columns={getColumns({
          bottlebooksFields: importData.bottlebooksFields,
        })}
      />
    </Flex>
  );
}

/**
 * Dynamically generate columns based on selected import fields
 * @param {object} props
 * @param {Array<{bottlebooksFieldName: string}>} props.bottlebooksFields
 * @returns
 */
function getColumns({ bottlebooksFields }) {
  return bottlebooksFields.map(({ bottlebooksFieldName }) => {
    return {
      bottlebooksFieldName,
      label: fieldsToImport.find((field) => field.key === bottlebooksFieldName)
        ?.label,
    };
  });
}

export function useReviewImport({ workspaceId, importId }) {
  const { data, error, loading } = useQuery(QUERY, {
    variables: { workspaceId, importId },
  });

  return {
    importId,
    ...afterQuery(data),
    error,
    loading,
  };
}

function afterQuery(data) {
  if (!data) return {};
  return { importData: data?.workspace?.dataImport };
}

const QUERY = gql`
  query ReviewPage($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        importId
        ...ChangesPreviewTable
      }
    }
  }
  ${ChangesPreviewTable.fragment}
`;
